import { useMemo, useState } from 'react';
import { Popover } from '@strivelabs/ui-core/v2';
import { Plus, Tag, X } from 'lucide-react';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { useLabels } from '@/hooks/useTasks';
import { EMPTY_VALUE, LABEL_CHIP } from '../taskTheme';
import { INLINE_FIELD } from './TaskInlinePicker';
import type { TaskLabel } from '@/types/task';

/**
 * Mirrors `LABEL_NAME_PATTERN` in `apps/server/src/label/constant.ts`, so a name
 * the server would refuse is caught while it is being typed.
 *
 * Keep the two in step: this copy is the one a person actually meets, so when
 * it is the stricter of the pair the server's rule never gets a say and a name
 * it would have accepted looks invalid.
 */
const LABEL_PATTERN = /^[a-z0-9_-](?:[a-z0-9 _-]*[a-z0-9_-])?$/;

/** Lowered and whitespace-collapsed, exactly as the server stores it. */
const normalise = (value: string) => value.trim().toLowerCase()
  .replace(/\s+/g, ' ');

interface TaskLabelsEditorProps {
  labels: TaskLabel[];
  disabled?: boolean;
  onChange: (names: string[]) => void;
}

/**
 * The task's labels.
 *
 * The workspace's existing labels are offered before a new one is made, which
 * is what keeps "paid search" and "paid-search" from both existing. Names are
 * replaced wholesale rather than added to, because that is what the API takes.
 */
export const TaskLabelsEditor = ({
  labels, disabled = false, onChange,
}: TaskLabelsEditorProps) => {
  const [ open, setOpen ] = useState(false);
  const [ query, setQuery ] = useState('');
  const { data: known = [] } = useLabels();

  const typed = normalise(query);
  const held = useMemo(() => labels.map((label) => label.name), [ labels ]);

  const offered = useMemo(
    () => known.filter((label) => label.name.includes(typed) && !held.includes(label.name)),
    [ known, typed, held ],
  );

  const creatable = typed
    && LABEL_PATTERN.test(typed)
    && !known.some((label) => label.name === typed);

  const toggle = (name: string) => onChange(held.includes(name)
    ? held.filter((each) => each !== name)
    : [ ...held, name ]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => { setOpen(next); setQuery(''); }}
      side="bottom"
      align="start"
      trigger={
        <button
          type="button"
          aria-label="Labels"
          disabled={disabled}
          className={cn(INLINE_FIELD, 'gap-1.5', disabled && 'pointer-events-none')}
        >
          {labels.length === 0 ? (
            <span className={`text-[13.5px] ${EMPTY_VALUE}`}>Add labels</span>
          ) : (
            <>
              <span className={cn(LABEL_CHIP, 'truncate')}>{labels[0].name}</span>
              {labels.length > 1 ? (
                <span
                  className={cn(LABEL_CHIP, 'shrink-0')}
                  title={labels.slice(1).map((label) => label.name).join(', ')}
                >
                  +{labels.length - 1}
                </span>
              ) : null}
            </>
          )}
        </button>
      }
    >
      <div className="w-[248px]">
        <div className="-m-1 mb-1.5 flex flex-wrap items-center gap-1.5 rounded-t-[10px] border-b border-[rgba(0,0,0,0.06)] bg-[#FBFAF7] px-2.5 py-2">
          {labels.map((label) => (
            <span key={label.id} className="inline-flex items-center gap-1.5 text-[13px] text-[#181818]">
              {label.name}
              <button
                type="button"
                aria-label={`Remove ${label.name}`}
                className="text-[10px] leading-none text-[#9A9A9A] hover:text-[#181818]"
                onClick={() => toggle(label.name)}
              >
                <X className="size-3" aria-hidden />
              </button>
            </span>
          ))}
          <input
            value={query}
            autoFocus
            aria-label="Find or create a label"
            placeholder="Find or create"
            className="min-w-[110px] flex-1 border-0 bg-transparent py-0.5 text-[13px] text-[#181818] outline-none placeholder:text-[#9A9A9A]"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && creatable) {
                event.preventDefault();
                toggle(typed);
                setQuery('');
              }
            }}
          />
        </div>

        <p className="px-[9px] pb-1.5 pt-1 text-[11.5px] text-[#9A9A9A]">
          Select an option or create one
        </p>

        <div className="max-h-[196px] overflow-y-auto">
          {offered.map((label) => (
            <button
              key={label.id}
              type="button"
              className="flex w-full items-center gap-2 rounded-[7px] px-[9px] py-[7px] text-left hover:bg-[#FBFBFB]"
              onClick={() => toggle(label.name)}
            >
              <span className={LABEL_CHIP}>{label.name}</span>
            </button>
          ))}

          {creatable ? (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-[7px] px-[9px] py-[7px] text-left text-[13.5px] text-[#9A9A9A] hover:text-[#696969]"
              onClick={() => { toggle(typed); setQuery(''); }}
            >
              <Plus className="size-[15px] shrink-0" aria-hidden />
              Create
              <span className={LABEL_CHIP}>{typed}</span>
            </button>
          ) : null}

          {offered.length === 0 && !creatable ? (
            <p className="flex items-center gap-2 px-[9px] py-2 text-[12.5px] text-[#9A9A9A]">
              <Tag className="size-3.5 shrink-0" aria-hidden />
              {typed ? 'That is not a label name' : 'No labels yet'}
            </p>
          ) : null}
        </div>
      </div>
    </Popover>
  );
};
